/**
 * TrainingDashboard — Lists available training modules and past sessions.
 *
 * Shows module cards with "Start Training" or "Resume" buttons,
 * plus a table of past sessions with certificate links.
 *
 * Route: /qms/training
 * API: GET /@qms/training/modules, GET /@qms/training/session
 *
 * Ref: CR-2026-027
 */
import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Card,
  Icon,
  Loader,
  Button,
  Table,
  Label,
  Message,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, getAuthHeaders, fetchApi } from '../../api';

export default function TrainingDashboard() {
  const history = useHistory();
  const [modules, setModules] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(null);

  useEffect(() => {
    Promise.all([
      fetchApi(`${getApiPath()}/@qms/training/modules`),
      fetchApi(`${getApiPath()}/@qms/training/session`),
    ])
      .then(([modsJson, sessJson]) => {
        setModules(modsJson.items || []);
        setSessions(sessJson.items || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const handleStart = async (moduleCode) => {
    setStarting(moduleCode);
    setError(null);
    try {
      const res = await fetch(`${getApiPath()}/@qms/training/session/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ module_code: moduleCode }),
      });
      if (res.status === 401) {
        window.location.href = `/login?return_url=${encodeURIComponent(window.location.pathname)}`;
        return;
      }
      const json = await res.json();
      if (!res.ok) {
        if (json.session_id) {
          // Already has in-progress session — navigate to it
          history.push(`/qms/training/session/${json.session_id}`);
          return;
        }
        throw new Error(json.error || `${res.status}`);
      }
      history.push(`/qms/training/session/${json.session_id}`);
    } catch (err) {
      setError(err.message);
      setStarting(null);
    }
  };

  // Find active session per module
  const activeSessionByModule = {};
  for (const s of sessions) {
    if (s.session_status === 'in_progress') {
      activeSessionByModule[s.module_code] = s;
    }
  }

  if (loading) {
    return (
      <Container style={{ paddingTop: '3em' }}>
        <Loader active inline="centered" content="Loading training modules..." />
      </Container>
    );
  }

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '3em' }}>
      <Helmet title="QMS Training" />
      <Link to="/qms">&larr; QMS Dashboard</Link>

      <div className="polyglot-page-header">
        <h1>QMS Training</h1>
        <p className="subtitle">Competence-based certification through hands-on practice</p>
      </div>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {/* Available modules */}
      <Header as="h2">Available Modules</Header>
      {modules.length === 0 ? (
        <Message info content="No training modules available." />
      ) : (
        <Card.Group itemsPerRow={2} stackable>
          {modules.map((mod) => {
            const active = activeSessionByModule[mod.module_code];
            return (
              <Card key={mod.module_code}>
                <Card.Content>
                  <Card.Header>{mod.title}</Card.Header>
                  <Card.Meta>
                    {mod.sop_reference} | Grants:{' '}
                    {(mod.grants_roles || []).join(', ')}
                  </Card.Meta>
                  <Card.Description>{mod.description}</Card.Description>
                </Card.Content>
                <Card.Content extra>
                  {active ? (
                    <Button
                      as={Link}
                      to={`/qms/training/session/${active.session_id}`}
                      color="orange"
                      fluid
                    >
                      <Icon name="play" /> Resume Training (Step{' '}
                      {active.current_step}/{(mod.steps || []).length})
                    </Button>
                  ) : (
                    <Button
                      color="green"
                      fluid
                      loading={starting === mod.module_code}
                      onClick={() => handleStart(mod.module_code)}
                    >
                      <Icon name="plus" /> Start Training
                    </Button>
                  )}
                </Card.Content>
              </Card>
            );
          })}
        </Card.Group>
      )}

      {/* Past sessions */}
      {sessions.length > 0 && (
        <>
          <Header as="h2" style={{ marginTop: '2em' }}>
            Your Sessions
          </Header>
          <Table compact celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Module</Table.HeaderCell>
                <Table.HeaderCell>Training Doc</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Progress</Table.HeaderCell>
                <Table.HeaderCell>Started</Table.HeaderCell>
                <Table.HeaderCell>Certificate</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {sessions.map((s) => (
                <Table.Row key={s.session_id}>
                  <Table.Cell>{s.module_title}</Table.Cell>
                  <Table.Cell>
                    <code>{s.training_doc_id}</code>
                  </Table.Cell>
                  <Table.Cell>
                    <Label
                      color={
                        s.session_status === 'completed'
                          ? 'green'
                          : s.session_status === 'in_progress'
                            ? 'orange'
                            : 'grey'
                      }
                      size="tiny"
                    >
                      {s.session_status}
                    </Label>
                  </Table.Cell>
                  <Table.Cell>
                    {s.completed_steps
                      ? `${(JSON.parse(typeof s.completed_steps === 'string' ? s.completed_steps : JSON.stringify(s.completed_steps)) || []).length}/${(s.module_steps || []).length}`
                      : `${Math.max(0, s.current_step - 1)}/${(s.module_steps || []).length}`}
                  </Table.Cell>
                  <Table.Cell>
                    {new Date(s.started_at).toLocaleDateString()}
                  </Table.Cell>
                  <Table.Cell>
                    {s.certificate_id ? (
                      <Button
                        as={Link}
                        to={`/qms/training/certificate/${encodeURIComponent(s.certificate_id)}`}
                        size="mini"
                        color="blue"
                      >
                        <Icon name="certificate" /> {s.certificate_id}
                      </Button>
                    ) : (
                      '—'
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}
    </Container>
  );
}
